import { VIDEO_STALL_MS } from '#~/livekit/constants.js';
import { MAGIC_MOMENT_POLL_MS } from './constants.js';

/**
 * Shared "has this video element actually decoded a frame" predicate.
 *
 * A device appearing in the bridge's resolved-devices map (or a LiveKit track
 * existing at all) only means the pipeline negotiated — it says nothing about
 * whether the browser ever painted a frame. `readyState` reaching
 * HAVE_CURRENT_DATA (2) with a non-zero `videoWidth` is the first point at
 * which the element is known to hold real decoded video, which is why the
 * connection test's "Video stream readable" check gates on this rather than
 * trusting the pipeline's own "resolved" signal.
 *
 * The e2e observation seam that lets a hardware-free journey supply this one
 * physical fact is deliberately not here yet: it is test infrastructure
 * (IRIS_E2E_TEST=1 only) and lands with the e2e port, not with the product code.
 *
 * Per rule: code-comments-required (ai.knowledge/global).
 */
export const hasDecodedFrame = (video: HTMLVideoElement): boolean =>
  video.readyState >= 2 && video.videoWidth > 0;

/** `hasDecodedFrame` applied to every `<video>` inside a container. */
export const containerHasDecodedFrame = (container: HTMLElement): boolean => {
  for (const video of container.querySelectorAll('video')) {
    if (hasDecodedFrame(video)) return true;
  }

  return false;
};

/**
 * Magic Moment liveness: a decoded last frame is not "live now".
 *
 * The level check above stays true after `docker pause` on a mounted element
 * (readyState 4, non-zero width, LIVE badge still painted). The connection
 * test keeps that check. Magic Moment needs frames to be arriving.
 *
 * `observe` records each in-container video's frame count and returns true
 * while a decoded video is on screen and some video advanced less than
 * VIDEO_STALL_MS ago, the app-wide stall window the video tile also uses.
 * One empty poll is not a freeze: slow (0.5 fps) or bursty streams leave gaps
 * longer than the 1s poll, and a replaced <video> has no baseline yet. With no
 * decoded video at all, nothing is on screen and it is not live. `peek`
 * returns the last verdict without recording, so Done/fallback cannot consume
 * the poll's evidence or disagree with the visible LIVE badge.
 *
 * Per rule: code-comments-required, reference-rules-in-comments,
 * absent-data-gates-permissively (ai.knowledge/global).
 */
export interface PlaybackSampler {
  readonly observe: (container: HTMLElement) => boolean;
  readonly peek: () => boolean;
}

const presentedFrameCount = (video: HTMLVideoElement): number | undefined => {
  const quality = video.getVideoPlaybackQuality?.();
  if (quality === undefined || !Number.isFinite(quality.totalVideoFrames)) {
    return undefined;
  }

  return quality.totalVideoFrames;
};

interface PlaybackSamplerOptions {
  /** Clock in milliseconds; tests pass a manual one. */
  readonly now?: () => number;
}

// Monotonic: a wall-clock step back (NTP, sleep/wake) must not hold a frozen
// stream live.
const monotonicNow = (): number => performance.now();

export const createPlaybackSampler = ({
  now = monotonicNow
}: PlaybackSamplerOptions = {}): PlaybackSampler => {
  const lastCount = new WeakMap<HTMLVideoElement, number>();
  let lastAdvancedAt: number | undefined;
  let lastLive = false;

  const observe = (container: HTMLElement): boolean => {
    const at = now();
    let decoded = false;
    let counted = false;

    for (const video of container.querySelectorAll('video')) {
      if (!hasDecodedFrame(video)) continue;
      decoded = true;
      const count = presentedFrameCount(video);
      if (count === undefined) continue;
      counted = true;
      const previous = lastCount.get(video);
      lastCount.set(video, count);
      if (previous !== undefined && count > previous) lastAdvancedAt = at;
    }

    if (!decoded) {
      lastLive = false;
    } else if (!counted) {
      // Per rule: absent-data-gates-permissively (ai.knowledge/global) — a
      // runtime that cannot count frames cannot prove a freeze, so it gets the
      // decoded-frame check rather than a locked-out camera.
      lastLive = true;
    } else {
      // Live needs at least one observed advancement, never a first sighting.
      // The observe that sees an advance runs up to one poll after the frame,
      // so the poll VIDEO_STALL_MS after it clears LIVE. Polls drift by a few
      // ms, so elapsed time is rounded to the nearest poll: the threshold sits
      // half a poll before the window, where no poll lands.
      lastLive =
        lastAdvancedAt !== undefined &&
        at - lastAdvancedAt < VIDEO_STALL_MS - MAGIC_MOMENT_POLL_MS / 2;
    }

    return lastLive;
  };

  return {
    observe,
    peek: (): boolean => lastLive
  };
};
